/*
  Product page: show the chosen variant's photo, card prices and Add to cart straight away. The theme re-renders the gallery from
  the server after an option is picked, which takes a moment; this finds the variant the picked
  options make (from the picker's embedded variant JSON) and moves the gallery to its photo at once,
  so the re-render arrives with that photo already showing. Hovering or touching an option starts
  loading its photo, so the switch doesn't show a blank frame.
  Loaded by snippets/product-variant-picker.liquid.
*/
(() => {
  if (window.mcsInstantVariantMedia) return;
  window.mcsInstantVariantMedia = true;

  const variantsOf = (picker) => {
    if (picker._mcsVariants !== undefined) return picker._mcsVariants;
    const data = picker.querySelector(':scope > script[data-variants]');
    try {
      picker._mcsVariants = data ? JSON.parse(data.textContent) : null;
    } catch (error) {
      picker._mcsVariants = null;
    }
    return picker._mcsVariants;
  };

  // The option values as they would be with `input` chosen (the others as currently checked)
  const optionsWith = (picker, input) => {
    const values = [];
    picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
      const position = Number(fieldset.getAttribute('data-option-index').replace('option', ''));
      const chosen = input && fieldset.contains(input) ? input : fieldset.querySelector('input:checked');
      values[position - 1] = chosen ? chosen.getAttribute('data-option-value') : null;
    });
    picker.querySelectorAll('select[data-option-index], select').forEach((select) => {
      const fieldset = select.closest('[data-option-index]');
      if (!fieldset) return;
      const position = Number(fieldset.getAttribute('data-option-index').replace('option', ''));
      values[position - 1] = select.selectedOptions[0]?.getAttribute('data-option-value') ?? select.value;
    });
    return values;
  };

  const variantFor = (picker, input) => {
    const variants = variantsOf(picker);
    if (!variants) return null;
    const values = optionsWith(picker, input);
    return variants.find((variant) => variant.options.every((value, i) => values[i] == null || value === values[i])) || null;
  };

  const galleryFor = (picker) => {
    const id = picker.id.replace(/^VariantPicker-/, '').replace(/-button$/, '');
    return document.getElementById(`ProductGallery-${id}`);
  };

  const slideFor = (gallery, mediaId) => {
    const slider = gallery?.querySelector('slider-element');
    if (!slider) return null;
    const slides = Array.from(slider.items || slider.children).filter((slide) => slide.clientWidth > 0);
    const index = slides.findIndex((slide) => slide.getAttribute('data-media-id') === String(mediaId));
    return index < 0 ? null : { slider, slide: slides[index], index };
  };

  const preload = (picker, input) => {
    const variant = variantFor(picker, input);
    const found = variant?.featured_media && slideFor(galleryFor(picker), variant.featured_media.id);
    const image = found?.slide.querySelector('img');
    if (image && image.loading === 'lazy') image.loading = 'eager';
  };

  const show = (picker, input) => {
    const variant = variantFor(picker, input);
    if (!variant || !variant.featured_media) return;
    const found = slideFor(galleryFor(picker), variant.featured_media.id);
    if (!found) return;
    const { slider, slide } = found;
    const image = slide.querySelector('img');
    if (image && image.loading === 'lazy') image.loading = 'eager';
    // One photo at a time (the thumbnail and slider layouts): scroll straight to it (the slider's own
    // scroll handler then updates its page and the thumbnails); layouts showing several are left alone
    const first = slider.items[0] || slide;
    const oneAtATime = slide.clientWidth >= slider.clientWidth * 0.9;
    if (oneAtATime) {
      // The slider scrolls smoothly by CSS, which phones (Safari especially) apply even to a scroll
      // asked to be instant: switch it off for this jump, then give it back
      const previous = slider.style.scrollBehavior;
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = slide.offsetLeft - first.offsetLeft;
      requestAnimationFrame(() => { slider.style.scrollBehavior = previous; });
    }
  };

  // The variant the options make with each of `inputs` chosen in its own option (others as checked)
  const variantWith = (picker, inputs) => {
    const variants = variantsOf(picker);
    if (!variants) return null;
    const values = optionsWith(picker, null);
    inputs.forEach((input) => {
      const fieldset = input.closest('fieldset[data-option-index]');
      if (!fieldset) return;
      const position = Number(fieldset.getAttribute('data-option-index').replace('option', ''));
      values[position - 1] = input.getAttribute('data-option-value');
    });
    return variants.find((variant) => variant.options.every((value, i) => value === values[i])) || null;
  };

  const money = (cents) => (window.theme?.Currency?.formatMoney ? theme.Currency.formatMoney(cents, theme.settings?.moneyFormat) : `$${(cents / 100).toFixed(2)}`);

  // Option cards with prices (the last option's cards, e.g. Size): each card's price for the newly
  // picked option (e.g. Material), at once rather than after the theme's re-render
  const repriceCards = (picker, picked) => {
    picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
      if (fieldset.contains(picked)) return;
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const priceEl = label?.querySelector('.variant-card__price');
        if (!priceEl) return;
        const variant = variantWith(picker, [picked, input]);
        // No such combination (e.g. Maple only in 58.55): no price, as the theme's re-render shows it
        if (!variant) { priceEl.textContent = ''; return; }
        priceEl.innerHTML = (variant.compare_at_price > variant.price ? `<s>${money(variant.compare_at_price)}</s> ` : '') + money(variant.price);
      });
    });
  };

  // The main price (and the sticky bar's) for the picked variant, from the server-rendered prices the
  // picker carries (data-mcs-variant-prices): the price element keeps its own layout classes, its
  // content and on-sale state are swapped
  const updatePrice = (picker, variant) => {
    const info = picker.closest('product-info');
    if (!info || !variant) return;
    const data = info.querySelector('script[data-mcs-variant-prices]');
    if (!data) return;
    let prices;
    try { prices = info._mcsPrices || (info._mcsPrices = JSON.parse(data.textContent)); } catch (error) { return; }
    const html = prices[variant.id];
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.price');
    if (!fresh) return;
    [`Price-${info.sectionId}-${info.productId}`, `StickyPrice-${info.sectionId}-${info.productId}`].forEach((id) => {
      const current = document.getElementById(id)?.querySelector('.price');
      if (!current) return;
      current.innerHTML = fresh.innerHTML;
      current.classList.toggle('price--on-sale', fresh.classList.contains('price--on-sale'));
    });
  };

  // The theme disables Add to cart until its re-render arrives. The form's variant id is set to the
  // picked variant straight away and the button put back (or Sold out), so it never flickers off and
  // a quick click adds the right variant.
  const settleForm = (picker, variant) => {
    const info = picker.closest('product-info');
    if (!info || !variant) return;
    [info.productForm, document.getElementById(`ProductFormInstallment-${info.sectionId}-${info.productId}`)].forEach((form) => {
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput && idInput.value !== String(variant.id)) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    const soldOut = window.theme?.variantStrings?.soldOut;
    info.productForm?.toggleSubmitButton?.(!variant.available, soldOut);
    info.productStickyForm?.toggleSubmitButton?.(!variant.available, soldOut);
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    const picker = input.closest?.('variant-picker');
    if (!picker || !picker.id.startsWith('VariantPicker-')) return;
    const picked = input.tagName === 'INPUT' ? input : null;
    show(picker, picked);
    if (picked) {
      repriceCards(picker, picked);
      // The chosen value's name after the option heading ("Material  Maple")
      const chosenName = picked.closest('fieldset')?.querySelector('.form__label .font-medium[id]');
      if (chosenName) chosenName.textContent = picked.getAttribute('data-option-value');
    }
    const variant = variantFor(picker, picked);
    updatePrice(picker, variant);
    // After the theme's own change handlers (which disable the button) have run
    setTimeout(() => settleForm(picker, variant), 0);
  }, true);

  // Mouse hover only: on iPhones a tap first fires the hover events, and any page change made in
  // them makes Safari treat the tap as a hover and hold back the click (the option needs a second
  // tap). Touch preloads after the tap has gone through instead (the 'change' handler sets eager).
  const warm = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const label = event.target.closest?.('variant-picker label[for]');
    if (!label) return;
    const input = document.getElementById(label.getAttribute('for'));
    const picker = label.closest('variant-picker');
    if (input && picker) preload(picker, input);
  };
  document.addEventListener('pointerover', warm, { passive: true });

  // Phones: priced option cards two to a row only when every card's name and price fit on one line;
  // otherwise that option lists one card per row (variant-cards--list, styled in
  // product-variant-picker.liquid), so no card's price drops under its name
  const phone = window.matchMedia('(max-width: 767px)');
  const fitCards = () => {
    document.querySelectorAll('.variant-picker-cards fieldset[data-option-index]').forEach((fieldset) => {
      const labels = Array.from(fieldset.querySelectorAll('.label-swatch')).filter((label) => label.querySelector('.variant-card__price'));
      if (!labels.length) return;
      fieldset.classList.remove('variant-cards--list');
      if (!phone.matches) return;
      // The name and price at their natural (unwrapped) widths, plus the gap and padding, against
      // the card's width
      const tooLong = labels.some((label) => {
        const name = label.querySelector('.variant-card__name');
        const price = label.querySelector('.variant-card__price');
        if (!name || !price) return false;
        const style = getComputedStyle(label);
        const previous = label.style.whiteSpace;
        label.style.whiteSpace = 'nowrap';
        const needed = name.scrollWidth + price.scrollWidth + (parseFloat(style.columnGap) || 0) + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        label.style.whiteSpace = previous;
        return needed > label.clientWidth + 1;
      });
      fieldset.classList.toggle('variant-cards--list', tooLong);
    });
  };
  let fitFrame = 0;
  const scheduleFit = () => { cancelAnimationFrame(fitFrame); fitFrame = requestAnimationFrame(fitCards); };
  const startFitting = () => {
    fitCards();
    window.addEventListener('resize', scheduleFit, { passive: true });
    document.fonts?.ready?.then(scheduleFit);
    // The theme re-renders the picker after an option is picked
    document.querySelectorAll('product-info').forEach((info) => new MutationObserver(scheduleFit).observe(info, { childList: true, subtree: true }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startFitting);
  else startFitting();

  // Phones: an option is chosen on the tap itself. iPhone Safari can hold back the click that a tap
  // on a swatch makes (treating the first tap as a hover, so a second tap was needed); a quick tap
  // that didn't scroll checks the option straight away. The click that may follow finds it already
  // chosen and changes nothing.
  let tapStart = null;
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') { tapStart = null; return; }
    const label = event.target.closest?.('variant-picker label[for]');
    tapStart = label ? { label, x: event.clientX, y: event.clientY, time: event.timeStamp } : null;
  }, { passive: true });
  document.addEventListener('pointercancel', () => { tapStart = null; }, { passive: true });
  document.addEventListener('pointerup', (event) => {
    const start = tapStart;
    tapStart = null;
    if (!start || event.pointerType !== 'touch') return;
    if (Math.abs(event.clientX - start.x) > 10 || Math.abs(event.clientY - start.y) > 10 || event.timeStamp - start.time > 600) return;
    if (!start.label.contains(document.elementFromPoint(event.clientX, event.clientY))) return;
    const input = document.getElementById(start.label.getAttribute('for'));
    if (!input || input.type !== 'radio' || input.checked || input.disabled) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }, { passive: true });
})();
